import { useState } from 'react';
import {
  Typography,
  TextField,
  MenuItem,
  Button,
  Stack,
  Alert,
  Box,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import LegalPage from '../../components/common/LegalPage';

// Structure mirrors https://jethl.com/opt-out per New Website.docx guidance.
// TODO: wire submission to a real backend endpoint — currently client-side only.

const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
  'Delaware','District of Columbia','Florida','Georgia','Idaho','Illinois',
  'Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland',
  'Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana',
  'Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York',
  'North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania',
  'Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah',
  'Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming',
];

const validationSchema = Yup.object({
  loanNumber: Yup.string().required('Loan number is required'),
  fullName: Yup.string().required('First and last name is required'),
  streetAddress: Yup.string().required('Property street address is required'),
  state: Yup.string().required('State is required'),
  email: Yup.string().email('Enter a valid email address').required('Email is required'),
  phone: Yup.string().required('Phone number is required'),
});

export default function OptOut() {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const formik = useFormik({
    initialValues: { loanNumber: '', fullName: '', streetAddress: '', state: '', email: '', phone: '' },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setError('');
      try {
        const res = await fetch('/api/optout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(values),
        });
        if (!res.ok) throw new Error('Server error');
        setSubmitted(true);
      } catch {
        setError('Something went wrong. Please try again or call us at 800-295-4439.');
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <LegalPage title="Opt-Out">
      <Typography variant="body1">
        Opt-outs can only be successfully processed if the information provided is accurate to your
        account. If you are unsure of any items on this form, please contact us at{' '}
        <strong>800-295-4439</strong>.
      </Typography>

      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
        By submitting the form below, you are requesting to limit the sharing of your personal
        information.
      </Typography>

      {error && <Alert severity="error" sx={{ mt: 2, mb: 1 }}>{error}</Alert>}
      {submitted ? (
        <Alert severity="success" sx={{ mt: 2 }}>
          Your opt-out request has been received. We will process it as quickly as possible.
        </Alert>
      ) : (
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ mt: 1 }}>
          <Stack spacing={2.5}>
            <TextField
              label="Loan Number"
              name="loanNumber"
              fullWidth
              value={formik.values.loanNumber}
              onChange={formik.handleChange}
              error={formik.touched.loanNumber && Boolean(formik.errors.loanNumber)}
              helperText={formik.touched.loanNumber && formik.errors.loanNumber}
            />
            <TextField
              label="First and Last Name"
              name="fullName"
              fullWidth
              value={formik.values.fullName}
              onChange={formik.handleChange}
              error={formik.touched.fullName && Boolean(formik.errors.fullName)}
              helperText={formik.touched.fullName && formik.errors.fullName}
            />
            <TextField
              label="Subject Property Street Address"
              name="streetAddress"
              fullWidth
              value={formik.values.streetAddress}
              onChange={formik.handleChange}
              error={formik.touched.streetAddress && Boolean(formik.errors.streetAddress)}
              helperText={formik.touched.streetAddress && formik.errors.streetAddress}
            />
            <TextField
              select
              label="Subject Address State"
              name="state"
              fullWidth
              value={formik.values.state}
              onChange={formik.handleChange}
              error={formik.touched.state && Boolean(formik.errors.state)}
              helperText={formik.touched.state && formik.errors.state}
            >
              {US_STATES.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Email Address"
              name="email"
              fullWidth
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
            <TextField
              label="Phone Number"
              name="phone"
              fullWidth
              value={formik.values.phone}
              onChange={formik.handleChange}
              error={formik.touched.phone && Boolean(formik.errors.phone)}
              helperText={formik.touched.phone && formik.errors.phone}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              sx={{ alignSelf: 'flex-start', px: 5 }}
            >
              OPT OUT
            </Button>
          </Stack>
        </Box>
      )}
    </LegalPage>
  );
}
